"use client"

import { usePage } from "@inertiajs/react"
import {
  ActivityHeart,
  BarChartSquare02,
  Calendar,
  Flag01,
  Mail01,
  MessageChatSquare,
  Phone,
  ShieldTick,
  Tool01,
  Tool02,
  UserPlus01,
  Users01,
} from "@untitledui/icons"

import { MobileNavigationHeader } from "@/components/console/mobile-header"
import { NavItem } from "@/components/console/nav-item"
import { UserChip } from "@/components/console/user-chip"
import { Wordmark } from "@/components/ui/wordmark"
import {
  adminAllowlistedNumbersPath,
  adminCallListsPath,
  adminCommitmentsPath,
  adminEmailHealthPath,
  adminEmailMessagesPath,
  adminEnquiriesPath,
  adminFixQueuePath,
  adminMsgFlowRunsPath,
  adminSalesforceJobsPath,
  adminUsersPath,
} from "@/routes"

/*
 * The rail: print.900 under the lamp (the `board` texture stack). The page you
 * are on is a sheet of paper pinned to it. Group heads are the instrument's
 * voice with a hairline filler; counts sit right-aligned in mono.
 */
export const RAIL_WIDTH = 238
/** The sheet overlaps the rail by this much, so the content column starts here. */
export const RAIL_OVERLAP = 14

interface NavGroup {
  label: string
  items: {
    label: string
    href: string
    icon: typeof MessageChatSquare
    count?: React.ReactNode
  }[]
}

const navigation: NavGroup[] = [
  {
    label: "Call list",
    items: [
      { label: "Lists", href: adminCallListsPath(), icon: Phone },
      { label: "Enquiries", href: adminEnquiriesPath(), icon: UserPlus01 },
      { label: "Fix queue", href: adminFixQueuePath(), icon: Tool01 },
      { label: "Commitments", href: adminCommitmentsPath(), icon: Flag01 },
      {
        label: "Allowlist",
        href: adminAllowlistedNumbersPath(),
        icon: ShieldTick,
      },
    ],
  },
  {
    label: "Message flows",
    items: [
      { label: "Runs", href: adminMsgFlowRunsPath(), icon: MessageChatSquare },
    ],
  },
  {
    label: "Email",
    items: [
      { label: "Ledger", href: adminEmailMessagesPath(), icon: Mail01 },
      { label: "Health", href: adminEmailHealthPath(), icon: ActivityHeart },
    ],
  },
  {
    label: "Salesforce",
    items: [{ label: "Jobs", href: adminSalesforceJobsPath(), icon: Calendar }],
  },
  {
    label: "Forms",
    items: [{ label: "Check-ins", href: "/dashboard", icon: BarChartSquare02 }],
  },
]

// The developers' pages; the server 404s them for everyone else, so the rail
// only shows them to a super admin.
const accessGroup: NavGroup = {
  label: "Access",
  items: [{ label: "Users", href: adminUsersPath(), icon: Users01 }],
}

// Hidden when Vite is built in production mode. The /good_job route itself
// is only mounted in dev and (staff-gated) staging — see config/routes.rb.
const devToolsGroup: NavGroup = {
  label: "Dev tools",
  items: [{ label: "GoodJob", href: "/good_job", icon: Tool02 }],
}

const navigationFor = (superAdmin: boolean): NavGroup[] => {
  const groups = superAdmin ? [...navigation, accessGroup] : navigation
  return import.meta.env.MODE === "production"
    ? groups
    : [...groups, devToolsGroup]
}

export const ConsoleRail = () => {
  const { url, props } = usePage()
  const visibleNavigation = navigationFor(
    props.auth?.user?.super_admin === true,
  )

  const content = (
    <aside
      className="board flex h-full w-full max-w-full flex-col overflow-auto px-4 pt-6 pb-4 lg:w-(--rail-width)"
      style={{ "--rail-width": `${RAIL_WIDTH}px` } as React.CSSProperties}
    >
      <div className="flex flex-col gap-[7px] px-2">
        <Wordmark size={26} className="text-stone-50" />
        <span className="text-onprint-quiet tracking-eyebrow font-mono text-[8.5px] leading-none font-medium uppercase">
          staff console
        </span>
      </div>

      <nav aria-label="Console" className="mt-7 flex flex-col gap-[18px]">
        {visibleNavigation.map((group) => (
          <div key={group.label} className="flex flex-col gap-0.5">
            <div className="mb-[5px] flex items-center gap-[7px] px-2">
              <span className="text-onprint-quiet font-mono text-[8px] leading-none font-medium tracking-[0.14em] uppercase">
                {group.label}
              </span>
              <span className="border-onprint-rule flex-1 border-t" />
            </div>
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <li key={item.label}>
                  <NavItem
                    icon={item.icon}
                    href={item.href}
                    count={item.count}
                    current={url.startsWith(item.href)}
                  >
                    {item.label}
                  </NavItem>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-onprint-rule mt-auto border-t pt-3">
        <UserChip />
      </div>
    </aside>
  )

  return (
    <>
      <MobileNavigationHeader>{content}</MobileNavigationHeader>

      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-10 lg:flex">
        {content}
      </div>

      <div
        aria-hidden="true"
        style={{ width: RAIL_WIDTH - RAIL_OVERLAP }}
        className="invisible hidden shrink-0 lg:block"
      />
    </>
  )
}
